import React from 'react';
import { graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';

import { usePagination, useUpdateUrlFromFilters, useWindowSize } from '../../utils/hooks';
import ResourcesPageLayout from '../../components/ResourcesPageLayout';
import Video from '../../components/Video';

import * as styles from './videos.module.scss';
import SEO from '../../components/SEO';

export const query = graphql`
  {
    allSanityResourceVideo(sort: { fields: date, order: DESC }) {
      nodes {
        title
        date
        url
        hideFromResourcesPage
      }
    }
  }
`;

interface ResourceVideo {
  title: string;
  date: string;
  url: string;
  hideFromResourcesPage?: boolean;
}

interface VideosProps {
  data: {
    allSanityResourceVideo: {
      nodes: Array<ResourceVideo>;
    };
  };
}

const videos = ({ data }: VideosProps): React.ReactElement => {
  const { t } = useTranslation();
  const {
    allSanityResourceVideo: { nodes: resourceVideos },
  } = data;

  const resourceVideosToShow = resourceVideos.filter(
    resourceVideo => !resourceVideo.hideFromResourcesPage,
  );

  const { width } = useWindowSize();

  const {
    currentPage,
    currentPageItems: currentPageVideos,
    getPageUrlPart,
    renderPagination,
  } = usePagination(resourceVideosToShow, 6);

  // Update url on filters state change
  useUpdateUrlFromFilters(() => [getPageUrlPart()], [currentPage]);

  let maxVideoWidth: undefined | number = undefined;
  if (width) {
    if (width < 520) {
      maxVideoWidth = (width - 40) * 2; // *2 for retina
    } else if (width < 960) {
      maxVideoWidth = width - 40 * 3; // *2 for retina
    } else {
      maxVideoWidth = 400;
    }
  }

  return (
    <ResourcesPageLayout currentSlug="videos">
      <SEO title={t('videos_page.title', 'Videos')} urlPath="/resources/videos" />
      <div className={styles.videosContainer}>
        {currentPageVideos.map((video, i) => (
          <Video
            key={i}
            url={video.url}
            title={video.title}
            date={video.date}
            className={styles.video}
            maxWidth={maxVideoWidth}
          ></Video>
        ))}
      </div>

      {renderPagination()}
    </ResourcesPageLayout>
  );
};

export default videos;
